import {useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import type {FunctionComponent} from 'react'
import React from 'react'
import {getDomainConfig} from '~/domains'
import {ValueForMoneyTagIcon} from '~/icons/value-for-money-tag'
import {getTranslation} from '~/localization/translation'
import type {BioProductBadge, ProductCoupon, RootLoaderResponse} from '~/types'

interface ProductCardBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  index: number
  badge?: BioProductBadge
  coupon?: ProductCoupon
  shouldRemoveLogoFromBadge?: boolean
}

const ProductCardBadge: FunctionComponent<ProductCardBadgeProps> = ({
  index,
  badge,
  coupon,
  shouldRemoveLogoFromBadge,
  ...attributes
}) => {
  const {language, featureFlags, domain, locale} = useRouteLoaderData('root') as RootLoaderResponse
  const translations = getTranslation(language)
  const {styles: stylesBydomain} = getDomainConfig(domain, locale)

  switch (badge) {
    case 'sponsored':
      return (
        <div {...attributes}>
          <div
            className={clsx(
              'tablet:devide-none z-40 flex h-9 items-center justify-center gap-2 p-2 desktop:divide-none',
              stylesBydomain.LISTPAGE_BADGE_SPONSORED_BG,
              stylesBydomain.LISTPAGE_BADGE_SPONSORED_TEXT,
              'listpage_badge_sponsored_bg listpage_badge_sponsored_text',
            )}
            data-testid="product-badge"
          >
            <p className="pl-0 text-base font-normal">{translations.Badge.sponsored}</p>
          </div>
        </div>
      )
    case 'bestValue':
      return (
        <div {...attributes}>
          <div
            className={clsx(
              'z-40 flex h-9 items-center justify-center gap-2 divide-x divide-white p-2 text-base font-medium',
              stylesBydomain.LISTPAGE_BADGE_BEST_VALUE_BG,
              stylesBydomain.LISTPAGE_BADGE_BEST_VALUE_TEXT,
              stylesBydomain.LISTPAGE_BADGE_BEST_VALUE_DIVIDER,
              'listpage_badge_best_value_bg listpage_badge_best_value_text listpage_badge_best_value_divider',
            )}
            data-testid="product-badge"
          >
            <span className={clsx(featureFlags?.useReorganizedCardLayout && 'text-[20px] font-bold')}>
              #{index + 1}
            </span>
            <p className="pl-2">{translations.Badge.bestValue}</p>
          </div>
        </div>
      )
    case 'winner':
      return (
        <div {...attributes}>
          <div
            className={clsx(
              'z-40 flex h-9 items-center justify-center gap-2 divide-x divide-white p-2 text-base font-medium',
              stylesBydomain.LISTPAGE_BADGE_WINNER_BG,
              stylesBydomain.LISTPAGE_BADGE_WINNER_TEXT,
              stylesBydomain.LISTPAGE_BADGE_WINNER_DIVIDER,
              'listpage_badge_winner_bg listpage_badge_winner_text listpage_badge_winner_divider',
            )}
            data-testid="product-badge"
          >
            <span className={clsx(featureFlags?.useReorganizedCardLayout && 'text-[20px] font-bold')}>
              #{index + 1}
            </span>
            <p className="pl-2">{translations.Badge.winner}</p>
          </div>
        </div>
      )
    case 'valueForMoney':
      return (
        <div {...attributes}>
          <div
            className={clsx(
              'z-40 flex h-9 items-center justify-center gap-2 divide-x divide-white p-2 text-base font-medium',
              stylesBydomain.LISTPAGE_BADGE_WINNER_BG,
              stylesBydomain.LISTPAGE_BADGE_WINNER_TEXT,
              stylesBydomain.LISTPAGE_BADGE_WINNER_DIVIDER,
            )}
            data-testid="product-badge"
          >
            <span className={clsx(featureFlags?.useReorganizedCardLayout && 'text-[20px] font-bold')}>
              #{index + 1}
            </span>
            <span className="flex shrink-0 flex-row items-center gap-2 pl-2 text-xl font-light">
              {translations.Badge.valueForMoney}
              <ValueForMoneyTagIcon className="shrink-0 fill-black" fill="black" />
            </span>
          </div>
        </div>
      )
    case 'greatChoice':
      return (
        <div {...attributes}>
          <div
            className={clsx(
              'z-40 flex h-9 items-center justify-center gap-2 divide-x divide-white p-2 text-base font-medium',
              stylesBydomain.LISTPAGE_BADGE_BEST_VALUE_BG,
              stylesBydomain.LISTPAGE_BADGE_BEST_VALUE_TEXT,
              stylesBydomain.LISTPAGE_BADGE_BEST_VALUE_DIVIDER,
            )}
            data-testid="product-badge"
          >
            <span className={clsx(featureFlags?.useReorganizedCardLayout && 'text-[20px] font-bold')}>
              #{index + 1}
            </span>
            <p className="pl-2 text-xl font-light">{translations.Badge.greatChoice}</p>
          </div>
        </div>
      )
    default:
      return (
        <div {...attributes}>
          <div
            className={clsx(
              'z-40 flex h-9 items-center justify-center gap-2 divide-x-2 divide-gray-500 p-2 text-base font-medium',
              stylesBydomain.LISTPAGE_BADGE_PLACEMENT_BG,
              stylesBydomain.LISTPAGE_BADGE_PLACEMENT_TEXT,
              'listpage_badge_placement_bg listpage_badge_placement_text',
            )}
            data-testid="product-badge"
          >
            <span className={clsx(featureFlags?.useReorganizedCardLayout && 'text-[20px] font-bold')}>
              #{index + 1}
            </span>
          </div>
        </div>
      )
  }
}

export default ProductCardBadge
